import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore } from '@/store/gameStore';
import { Lightbulb, ChevronDown, ChevronUp, Eye, EyeOff, Sparkles, Target, AlertTriangle, Swords, Crown, Settings2, ArrowRight, Shield, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';

// Piece names for display
const PIECE_NAMES = {
  p: 'Pawn',
  n: 'Knight',
  b: 'Bishop',
  r: 'Rook',
  q: 'Queen',
  k: 'King'
};

// Convert algebraic notation to friendly text
function friendlyMoveDescription(move) {
  if (!move) return '';
  
  if (move.san === 'O-O') return 'Castle kingside';
  if (move.san === 'O-O-O') return 'Castle queenside';
  
  const piece = PIECE_NAMES[move.piece] || 'Piece';
  const to = move.to?.toUpperCase() || '';
  
  if (move.captured) {
    const capturedPiece = PIECE_NAMES[move.captured] || 'piece';
    return `${piece} takes ${capturedPiece} on ${to}`;
  }
  
  return `${piece} to ${to}`;
}

// Coach depth levels
const COACH_LEVELS = [
  { value: 1, label: '1 Move', description: 'Just the best move' },
  { value: 3, label: '3 Moves', description: 'Your move + response + reply' },
  { value: 5, label: '5 Moves', description: 'Full tactical sequence' }
];

const CoachFeedback = () => {
  const { 
    coachEnabled, 
    suggestedMove,
    boardTheme,
    gameStatus,
    evaluation,
    isAIThinking,
    coachLevel,
    setCoachLevel,
    arrowCount,
    setArrowCount,
    showDangerHighlights,
    setShowDangerHighlights
  } = useGameStore();
  
  const [showPrediction, setShowPrediction] = useState(false);
  const [showLevelSelector, setShowLevelSelector] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  
  // Default coach level if not set
  const currentLevel = coachLevel || 3;
  const currentArrowCount = arrowCount || 3;
  
  if (!coachEnabled || gameStatus !== 'playing') return null;
  
  const formatEval = (eval_) => {
    if (Math.abs(eval_) >= 10000) return eval_ > 0 ? '+M' : '-M';
    const score = eval_ / 100;
    return score >= 0 ? `+${score.toFixed(1)}` : score.toFixed(1);
  };

  const getEvalColor = () => {
    if (evaluation > 100) return 'text-green-400';
    if (evaluation < -100) return 'text-red-400';
    return boardTheme === 'cosmic' ? 'text-gray-300' : 'text-gray-600';
  };

  const getEvalMessage = () => {
    if (suggestedMove?.mateIn) return `Mate in ${suggestedMove.mateIn}!`;
    if (evaluation > 500) return "Winning position!";
    if (evaluation > 200) return "Clear advantage";
    if (evaluation > 100) return "Slight edge";
    if (evaluation < -500) return "Losing position";
    if (evaluation < -200) return "Tough spot";
    if (evaluation < -100) return "Slightly worse";
    return "Even game";
  };

  // Get PV line from suggested move
  const pvLine = suggestedMove?.pvLine || [];
  const displayMoves = pvLine.slice(0, currentLevel);

  // Check if mate is near
  const hasMateAlert = suggestedMove?.mateIn && suggestedMove.mateIn <= 5;
  
  return (
    <div 
      className={`
        rounded-xl p-4 space-y-3
        ${hasMateAlert 
          ? 'bg-gradient-to-r from-yellow-900/50 to-orange-900/50 border-2 border-yellow-500/50 animate-pulse'
          : boardTheme === 'cosmic' 
            ? 'bg-slate-900/80 border border-purple-500/30' 
            : 'bg-amber-50 border border-amber-200'
        }
      `}
      data-testid="coach-feedback"
    >
      {/* Header with Mate Alert */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {hasMateAlert ? (
            <Crown className="w-5 h-5 text-yellow-400 animate-bounce" />
          ) : (
            <Sparkles className={`w-5 h-5 ${boardTheme === 'cosmic' ? 'text-cyan-400' : 'text-amber-600'}`} />
          )}
          <h3 className={`font-bold tracking-wide ${hasMateAlert ? 'text-yellow-400' : boardTheme === 'cosmic' ? 'text-cyan-400' : 'text-amber-800'}`}>
            {hasMateAlert ? 'CHECKMATE ALERT!' : 'COACH'}
          </h3>
        </div>
        <div className="flex items-center gap-3">
          {/* Visual Settings Button */}
          <button
            onClick={() => setShowSettings(!showSettings)}
            className={`
              p-1.5 rounded-lg transition-colors
              ${boardTheme === 'cosmic'
                ? 'bg-slate-800/50 text-gray-400 hover:bg-slate-700 hover:text-cyan-400 border border-slate-700'
                : 'bg-amber-100/50 text-amber-600 hover:bg-amber-200 border border-amber-200'
              }
              ${showSettings ? (boardTheme === 'cosmic' ? 'bg-cyan-900/50 text-cyan-400' : 'bg-amber-200 text-amber-700') : ''}
            `}
            title="Visual Settings"
            data-testid="coach-settings-btn"
          >
            <Settings2 className="w-4 h-4" />
          </button>
          
          {/* Coach Level Selector */}
          <div className="relative">
            <button
              onClick={() => setShowLevelSelector(!showLevelSelector)}
              className={`
                flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium transition-colors
                ${boardTheme === 'cosmic'
                  ? 'bg-purple-900/50 text-purple-300 hover:bg-purple-900/70 border border-purple-500/30'
                  : 'bg-amber-100 text-amber-700 hover:bg-amber-200 border border-amber-300'
                }
              `}
              data-testid="coach-level-btn"
            >
              <ArrowRight className="w-3.5 h-3.5" />
              {currentLevel} Move{currentLevel > 1 ? 's' : ''}
              <ChevronDown className={`w-3 h-3 transition-transform ${showLevelSelector ? 'rotate-180' : ''}`} />
            </button>
            
            <AnimatePresence>
              {showLevelSelector && (
                <motion.div
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  className={`
                    absolute right-0 top-full mt-1 z-10 rounded-lg overflow-hidden shadow-xl
                    ${boardTheme === 'cosmic'
                      ? 'bg-slate-800 border border-purple-500/30'
                      : 'bg-white border border-amber-200'
                    }
                  `}
                >
                  {COACH_LEVELS.map((level) => (
                    <button
                      key={level.value}
                      onClick={() => {
                        setCoachLevel?.(level.value);
                        setShowLevelSelector(false);
                        // Auto-show prediction when selecting 3 or 5
                        if (level.value > 1) setShowPrediction(true);
                      }}
                      className={`
                        w-full px-4 py-2 text-left flex flex-col transition-colors
                        ${currentLevel === level.value
                          ? boardTheme === 'cosmic'
                            ? 'bg-purple-600/30 text-white'
                            : 'bg-amber-100 text-amber-900'
                          : boardTheme === 'cosmic'
                            ? 'text-gray-300 hover:bg-slate-700'
                            : 'text-gray-700 hover:bg-gray-50'
                        }
                      `}
                      data-testid={`coach-level-${level.value}`}
                    >
                      <span className="font-semibold text-sm">{level.label}</span>
                      <span className={`text-xs ${boardTheme === 'cosmic' ? 'text-gray-500' : 'text-gray-500'}`}>
                        {level.description}
                      </span>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          {/* Eval Display */}
          <div className="text-right">
            <div className={`text-sm font-mono font-bold ${hasMateAlert ? 'text-yellow-400' : getEvalColor()}`}>
              {formatEval(evaluation)}
            </div>
            <div className={`text-xs ${boardTheme === 'cosmic' ? 'text-gray-500' : 'text-gray-500'}`}>
              {getEvalMessage()}
            </div>
          </div>
        </div>
      </div>
      
      {/* Visual Settings Panel */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className={`
              rounded-lg p-3 space-y-3 overflow-hidden
              ${boardTheme === 'cosmic'
                ? 'bg-slate-800/70 border border-slate-700'
                : 'bg-white border border-amber-200'
              }
            `}
          >
            <div className={`text-xs font-semibold uppercase tracking-wider ${boardTheme === 'cosmic' ? 'text-gray-400' : 'text-gray-500'}`}>
              Visual Settings
            </div>
            
            {/* Arrow Count Slider */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ArrowRight className={`w-4 h-4 ${boardTheme === 'cosmic' ? 'text-cyan-400' : 'text-amber-600'}`} />
                  <span className={`text-sm ${boardTheme === 'cosmic' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Arrows on Board
                  </span>
                </div>
                <span className={`text-sm font-mono font-bold ${boardTheme === 'cosmic' ? 'text-cyan-400' : 'text-amber-700'}`}>
                  {currentArrowCount}
                </span>
              </div>
              <Slider
                value={[currentArrowCount]}
                onValueChange={(value) => setArrowCount?.(value[0])}
                min={1}
                max={5}
                step={1}
                className="w-full"
                data-testid="arrow-count-slider"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>Less</span>
                <span>More</span>
              </div>
            </div>
            
            {/* Danger Highlights Toggle */}
            <div className="flex items-center justify-between pt-2 border-t border-slate-700/50">
              <div className="flex items-center gap-2">
                {showDangerHighlights ? (
                  <ShieldAlert className={`w-4 h-4 ${boardTheme === 'cosmic' ? 'text-red-400' : 'text-red-500'}`} />
                ) : (
                  <Shield className={`w-4 h-4 ${boardTheme === 'cosmic' ? 'text-gray-500' : 'text-gray-400'}`} />
                )}
                <span className={`text-sm ${boardTheme === 'cosmic' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Danger Highlights
                </span>
              </div>
              <button
                onClick={() => setShowDangerHighlights?.(!showDangerHighlights)}
                className={`
                  relative w-10 h-5 rounded-full transition-colors
                  ${showDangerHighlights
                    ? boardTheme === 'cosmic' ? 'bg-cyan-500' : 'bg-amber-500'
                    : boardTheme === 'cosmic' ? 'bg-slate-600' : 'bg-gray-300'
                  }
                `}
                data-testid="danger-highlights-toggle"
              >
                <motion.div
                  className="absolute top-0.5 w-4 h-4 bg-white rounded-full shadow"
                  animate={{ left: showDangerHighlights ? '22px' : '2px' }}
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                />
              </button>
            </div>
            
            {/* Legend */}
            <div className={`pt-2 border-t ${boardTheme === 'cosmic' ? 'border-slate-700/50' : 'border-amber-200'}`}>
              <div className={`text-xs ${boardTheme === 'cosmic' ? 'text-gray-500' : 'text-gray-500'} mb-2`}>
                Arrow Legend:
              </div>
              <div className="flex flex-wrap gap-3 text-xs">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-0.5 bg-green-400 rounded" />
                  <span className={boardTheme === 'cosmic' ? 'text-gray-400' : 'text-gray-600'}>Your move</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-0.5 bg-orange-500 rounded" style={{ borderTop: '2px dashed' }} />
                  <span className={boardTheme === 'cosmic' ? 'text-gray-400' : 'text-gray-600'}>Opponent</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-0.5 bg-cyan-400 rounded" />
                  <span className={boardTheme === 'cosmic' ? 'text-gray-400' : 'text-gray-600'}>Follow-up</span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mate Alert Banner */}
      {hasMateAlert && (
        <div className="p-3 rounded-lg bg-yellow-500/20 border border-yellow-500/50">
          <div className="flex items-center gap-2 text-yellow-400 font-bold">
            <AlertTriangle className="w-4 h-4" />
            Checkmate in {suggestedMove.mateIn} move{suggestedMove.mateIn > 1 ? 's' : ''}!
          </div>
          <p className="text-xs text-yellow-300/80 mt-1">
            Follow the suggested moves carefully to win.
          </p>
        </div>
      )}

      {/* Suggested Move or Loading */}
      {isAIThinking ? (
        <div className={`flex items-center gap-3 p-3 rounded-lg ${boardTheme === 'cosmic' ? 'bg-slate-800/50' : 'bg-gray-100'}`}>
          <div className="w-5 h-5 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
          <span className={`text-sm ${boardTheme === 'cosmic' ? 'text-gray-400' : 'text-gray-600'}`}>
            Opponent thinking...
          </span>
        </div>
      ) : suggestedMove ? (
        <div className="space-y-3">
          {/* Move Suggestion Card */}
          <div className={`
            p-4 rounded-lg
            ${hasMateAlert
              ? 'bg-yellow-500/20 border border-yellow-500/40'
              : boardTheme === 'cosmic'
                ? 'bg-gradient-to-r from-cyan-900/40 to-purple-900/40 border border-cyan-500/30'
                : 'bg-green-100 border border-green-300'
            }
          `}>
            <div className="flex items-start gap-3">
              <div className={`
                p-2 rounded-lg
                ${hasMateAlert ? 'bg-yellow-500/30' : boardTheme === 'cosmic' ? 'bg-cyan-500/20' : 'bg-green-200'}
              `}>
                {suggestedMove.captured ? (
                  <Swords className={`w-5 h-5 ${hasMateAlert ? 'text-yellow-400' : boardTheme === 'cosmic' ? 'text-cyan-400' : 'text-green-700'}`} />
                ) : suggestedMove.san?.includes('+') ? (
                  <Target className={`w-5 h-5 ${hasMateAlert ? 'text-yellow-400' : boardTheme === 'cosmic' ? 'text-cyan-400' : 'text-green-700'}`} />
                ) : (
                  <Lightbulb className={`w-5 h-5 ${hasMateAlert ? 'text-yellow-400' : boardTheme === 'cosmic' ? 'text-cyan-400' : 'text-green-700'}`} />
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs uppercase tracking-wider ${hasMateAlert ? 'text-yellow-400/70' : boardTheme === 'cosmic' ? 'text-cyan-400/70' : 'text-green-700'}`}>
                    {hasMateAlert ? 'Winning Move' : 'Best Move'}
                  </span>
                  {suggestedMove.isBookMove && (
                    <span className={`text-xs px-2 py-0.5 rounded ${boardTheme === 'cosmic' ? 'bg-purple-500/30 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                      Opening
                    </span>
                  )}
                </div>
                
                {/* Friendly description */}
                <div className={`text-lg font-semibold mb-1 ${hasMateAlert ? 'text-yellow-200' : boardTheme === 'cosmic' ? 'text-white' : 'text-gray-900'}`}>
                  {friendlyMoveDescription(suggestedMove)}
                </div>
                
                {/* Chess notation */}
                <div className={`text-sm font-mono ${hasMateAlert ? 'text-yellow-300/70' : boardTheme === 'cosmic' ? 'text-cyan-300/70' : 'text-green-600'}`}>
                  {suggestedMove.from?.toUpperCase()} → {suggestedMove.to?.toUpperCase()}
                  {suggestedMove.san && ` (${suggestedMove.san})`}
                </div>
              </div>
            </div>
            
            {/* Explanation */}
            {suggestedMove.explanation && (
              <div className="mt-3">
                <p className={`text-sm ${hasMateAlert ? 'text-yellow-200/90' : boardTheme === 'cosmic' ? 'text-gray-300' : 'text-gray-700'}`}>
                  {suggestedMove.explanation}
                </p>
              </div>
            )}
          </div>

          {/* Principal Variation - Show if level > 1 and we have PV moves */}
          {currentLevel > 1 && displayMoves.length > 1 && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPrediction(!showPrediction)}
                className={`
                  w-full justify-between
                  ${boardTheme === 'cosmic'
                    ? 'bg-slate-800/50 border-purple-500/30 text-purple-300 hover:bg-purple-900/30'
                    : 'bg-white border-amber-300 text-amber-700 hover:bg-amber-100'
                  }
                `}
                data-testid="show-prediction-btn"
              >
                <span className="flex items-center gap-2">
                  {showPrediction ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  {showPrediction ? 'Hide sequence' : `Show ${currentLevel}-move sequence`}
                </span>
                {showPrediction ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>

              {/* Prediction Line */}
              <AnimatePresence>
                {showPrediction && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className={`
                      p-3 rounded-lg overflow-hidden
                      ${boardTheme === 'cosmic'
                        ? 'bg-slate-800/50 border border-slate-700'
                        : 'bg-gray-100 border border-gray-200'
                      }
                    `}
                  >
                    <div className={`text-xs uppercase tracking-wider mb-3 flex items-center justify-between ${boardTheme === 'cosmic' ? 'text-gray-500' : 'text-gray-500'}`}>
                      <span>Principal Variation ({currentLevel} moves)</span>
                      {suggestedMove.evaluation?.depth && (
                        <span className="text-xs opacity-60">Depth {suggestedMove.evaluation.depth}</span>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      {displayMoves.map((move, idx) => (
                        <motion.div 
                          key={idx}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.1 }}
                          className={`
                            flex items-center gap-3 p-2.5 rounded-lg
                            ${idx === 0
                              ? boardTheme === 'cosmic'
                                ? 'bg-cyan-500/20 border border-cyan-500/30'
                                : 'bg-green-100 border border-green-200'
                              : boardTheme === 'cosmic'
                                ? 'bg-slate-700/50'
                                : 'bg-white border border-gray-200'
                            }
                          `}
                        >
                          {/* Move number */}
                          <span className={`
                            text-xs min-w-[28px] h-7 rounded-full flex items-center justify-center font-bold
                            ${idx % 2 === 0 
                              ? 'bg-white text-gray-900 border border-gray-300' 
                              : 'bg-gray-800 text-white'
                            }
                          `}>
                            {idx % 2 === 0 ? '→' : '←'}
                          </span>
                          
                          {/* Player indicator */}
                          <span className={`text-xs font-medium w-8 ${idx % 2 === 0 ? 'text-green-500' : 'text-red-400'}`}>
                            {idx % 2 === 0 ? 'You' : 'AI'}
                          </span>
                          
                          {/* Move description */}
                          <span className={`flex-1 text-sm ${boardTheme === 'cosmic' ? 'text-gray-200' : 'text-gray-700'}`}>
                            {friendlyMoveDescription(move)}
                          </span>
                          
                          {/* SAN notation */}
                          <span className={`
                            text-xs font-mono px-2 py-1 rounded
                            ${boardTheme === 'cosmic' ? 'bg-slate-600 text-gray-300' : 'bg-gray-200 text-gray-600'}
                          `}>
                            {move.san}
                          </span>
                        </motion.div>
                      ))}
                    </div>
                    
                    {/* Hint about analysis */}
                    <p className={`mt-3 text-xs ${boardTheme === 'cosmic' ? 'text-gray-500' : 'text-gray-500'}`}>
                      💡 This is the engine's best continuation. Actual play may vary.
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </>
          )}
        </div>
      ) : (
        <div className={`flex items-center gap-3 p-3 rounded-lg ${boardTheme === 'cosmic' ? 'bg-slate-800/50' : 'bg-gray-100'}`}>
          <div className="w-5 h-5 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
          <span className={`text-sm ${boardTheme === 'cosmic' ? 'text-gray-400' : 'text-gray-600'}`}>
            Analyzing position...
          </span>
        </div>
      )}
    </div>
  );
};

export default CoachFeedback;
